/**
 * Central do Converte Facil: tudo de todas as empresas e os totais gerais
 * (decisao do dono, 24/09/2026). So `platform_admin` chega aqui; a rota ja
 * e protegida em routes.tsx.
 *
 * Hierarquia, de cima para baixo:
 *  1. Faixa de identidade da Central com a janela do cabecalho.
 *  2. Comissao geral do periodo (a figura principal: e o faturamento do
 *     Converte Facil, com tendencia e variacao) ao lado do contador grande
 *     de empresas por status.
 *  3. Oito totais com a variacao contra o periodo anterior: recuperado,
 *     vendas, liquido das empresas, taxa, custos, margem, entradas, estornos.
 *  4. "O que fazer agora" (insights de todas as empresas) e o ritmo com a
 *     economia unitaria (custo por venda, comissao por venda), lado a lado.
 *  5. Barras comissao x custo (dia ou semana) e o anel de custos por canal.
 *  6. Ranking top 5 por margem e por recuperado.
 *  7. Tabela por empresa: busca, filtro, ordenacao, paginacao, custo com
 *     detalhe em modal, "Abrir CRM" e "Cadastro".
 *  8. Alertas priorizados com acao e atividade recente, lado a lado.
 *  9. Rodape com o periodo, a base da comparacao e a regra da comissao.
 *
 * Decisoes nao obvias:
 *  - UM recurso so (`/api/admin/overview`): a Central e uma foto consolidada,
 *    e seis rotas com seis estados de erro deixariam meia pagina em branco
 *    por causa de um agregado que falhou.
 *  - O fetcher depende so de `range`. A empresa em foco no cabecalho NAO
 *    recorta a Central; troca-la nao recarrega nada. "Abrir CRM" e o caminho
 *    inverso: troca a empresa em foco e leva a visao geral preservando a
 *    busca da URL (o periodo).
 *  - `useCompany().companies` e a fonte de logo e segmento; a API so traz
 *    numeros por `companyId`.
 *  - O estado da tabela mora AQUI para "O que fazer agora" e o ranking
 *    abrirem a tabela ja filtrada e ordenada, rolando ate ela.
 *  - Toda leitura parte de um fato ja ocorrido (abandono ha 30 min, plano
 *    vencido). Nao existe bloco de "vai vencer" nem lembrete pre-vencimento:
 *    sem o fato nao ha recuperacao e, portanto, nao ha comissao.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { Badge, HeroBanner } from '../../../components/ui';
import { useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatCount, formatDate } from '../../../lib/format';
import { describeSelection, rangeDays } from '../../../lib/period';
import { usePeriod } from '../../../lib/periodContext';
import { agruparSerie, granularidadeDaJanela } from '../agrupar';
import { fetchPlatformOverview } from '../api';
import { diasEntre } from '../comparar';
import { AlertasDaPlataforma } from '../components/AlertasDaPlataforma';
import { AtividadeDaPlataforma } from '../components/AtividadeDaPlataforma';
import { ComissaoGeral, GradeDeTotais } from '../components/CartoesTotais';
import { ContadorDeEmpresas } from '../components/ContadorDeEmpresas';
import { CustosPorCanal } from '../components/CustosPorCanal';
import { DetalheDeCustos } from '../components/DetalheDeCustos';
import { EsqueletoCentral } from '../components/EsqueletoCentral';
import { GraficoComissaoCusto } from '../components/GraficoComissaoCusto';
import { OQueFazerAgoraCentral } from '../components/OQueFazerAgoraCentral';
import { RankingDeEmpresas } from '../components/RankingDeEmpresas';
import { RitmoEEconomia } from '../components/RitmoEEconomia';
import { TabelaPorEmpresa } from '../components/TabelaPorEmpresa';
import { gerarInsightsDaCentral, type AcaoDoInsight } from '../insights';
import { ESTADO_INICIAL_DA_TABELA, type ColunaDaTabela, type EstadoDaTabela } from '../tabela';
import type { CompanyRow } from '../types';

function rolarAte(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function AdminCentral(): JSX.Element {
  const { companies, selectCompany } = useCompany();
  const { range, selection } = usePeriod();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.title = 'Central · Converte Fácil';
  }, []);

  const buscar = useCallback((signal: AbortSignal) => fetchPlatformOverview(range, signal), [range]);
  const { state, reload } = useApiResource(buscar, 30_000);

  const [tabela, setTabela] = useState<EstadoDaTabela>(ESTADO_INICIAL_DA_TABELA);
  const [detalhe, setDetalhe] = useState<CompanyRow | null>(null);
  const fecharDetalhe = useCallback((): void => setDetalhe(null), []);

  const dias = rangeDays(range);
  const dados = state.status === 'success' ? state.data : null;
  const serie = useMemo(() => (dados === null ? null : agruparSerie(dados.daily, granularidadeDaJanela(dias))), [dados, dias]);
  const insights = useMemo(() => (dados === null ? [] : gerarInsightsDaCentral(dados)), [dados]);

  const abrirCrm = useCallback(
    (companyId: string): void => {
      selectCompany(companyId);
      navigate({ pathname: '/', search: location.search });
    },
    [selectCompany, navigate, location.search],
  );

  const abrirTabela = useCallback((estado: Partial<EstadoDaTabela>): void => {
    setTabela((atual) => ({ ...atual, ...estado, offset: 0 }));
    rolarAte('por-empresa');
  }, []);

  const verNaTabela = useCallback((coluna: ColunaDaTabela): void => abrirTabela({ filtro: 'todas', busca: '', ordem: { coluna, desc: true } }), [abrirTabela]);

  const detalharCustosDe = useCallback(
    (companyId: string): void => {
      const row = dados?.companies.find((r) => r.companyId === companyId) ?? null;
      setDetalhe(row);
    },
    [dados],
  );

  const executarAcao = (acao: AcaoDoInsight): void => {
    switch (acao.kind) {
      case 'rota':
        navigate({ pathname: acao.rota, search: location.search });
        return;
      case 'tabela':
        abrirTabela(acao.estado);
        return;
      case 'ancora':
        rolarAte(acao.id);
        return;
    }
  };

  const periodoTexto = describeSelection(selection);

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="grafico"
        title="Central Converte Fácil"
        badges={
          <>
            <Badge tone="brand" dot>
              Administração
            </Badge>
            <Badge tone="neutral" icon="escudo">
              Só depois do fato
            </Badge>
          </>
        }
        description="Todas as empresas, recuperação geral, comissão, custos da operação e margem no período. Cada número nasce de um checkout já abandonado ou de um plano já vencido."
        aside={
          <>
            <RefreshingDot active={state.status === 'success' && state.refreshing} />
            <Badge tone="neutral" icon="calendario">
              {periodoTexto}
            </Badge>
          </>
        }
      />

      {state.status === 'loading' ? <EsqueletoCentral /> : null}

      {state.status === 'error' ? <ErrorPanel title="Não foi possível carregar a Central" error={state.error} onRetry={reload} /> : null}

      {dados !== null && serie !== null ? (
        <>
          {state.status === 'success' && state.simulated ? (
            <SimulatedNotice>
              Esta tela mostra um conjunto fictício e fixo para todas as empresas do catálogo, com tarifas de exemplo para os custos. Nenhum número aqui vem do banco nem de
              fatura.
            </SimulatedNotice>
          ) : null}

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <ComissaoGeral
                totals={dados.totals}
                anterior={dados.previousTotals}
                tendenciaComissao={serie.comissaoCents}
                rotuloTendencia={`Comissão por ${serie.granularidade} no período`}
                periodo={periodoTexto}
              />
            </div>
            <ContadorDeEmpresas
              statusCounts={dados.statusCounts}
              novas={dados.newCompaniesCount}
              comAlerta={new Set(dados.alerts.map((a) => a.companyId)).size}
              periodo={periodoTexto}
            />
          </div>

          <GradeDeTotais totals={dados.totals} anterior={dados.previousTotals} />

          <div className="grid gap-6 lg:grid-cols-3">
            <OQueFazerAgoraCentral insights={insights} onAcao={executarAcao} className="lg:col-span-2" />
            <RitmoEEconomia totals={dados.totals} anterior={dados.previousTotals} dias={diasEntre(dados.periodStart, dados.periodEnd)} />
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <GraficoComissaoCusto serie={serie} className="lg:col-span-2" />
            <CustosPorCanal totals={dados.totals} anterior={dados.previousTotals} tariffs={dados.tariffs} />
          </div>

          <RankingDeEmpresas rows={dados.companies} companies={companies} totalComissaoCents={dados.totals.commissionCents} onVerTabela={verNaTabela} />

          <TabelaPorEmpresa rows={dados.companies} companies={companies} estado={tabela} onEstado={setTabela} onAbrirCrm={abrirCrm} onDetalharCustos={setDetalhe} />

          <div className="grid gap-6 lg:grid-cols-2">
            <AlertasDaPlataforma alerts={dados.alerts} companies={companies} onDetalharCustos={detalharCustosDe} />
            <AtividadeDaPlataforma events={dados.recentEvents} companies={companies} />
          </div>

          <p className="text-xs text-ink-subtle">
            Período de {formatDate(dados.periodStart)} a {formatDate(dados.periodEnd)} ({periodoTexto}) · {formatCount(dados.companies.length)}{' '}
            {dados.companies.length === 1 ? 'empresa' : 'empresas'}
            {dados.previousPeriod === null
              ? ' · sem período anterior para comparar'
              : ` · comparado com ${formatDate(dados.previousPeriod.start)} a ${formatDate(dados.previousPeriod.end)}, o período anterior de mesmo tamanho`}
            {' · '}A comissão existe porque recuperamos quem já tinha saído: nenhum toque acontece antes do abandono ou do vencimento.
          </p>

          <DetalheDeCustos row={detalhe} tariffs={dados.tariffs} periodo={periodoTexto} onClose={fecharDetalhe} />
        </>
      ) : null}
    </section>
  );
}
